import { queryKeys } from '@/lib/query-keys';
import { organizationClient } from '@/lib/api/ts-rest';
import type { SyncOrganizationResult } from '@octave/contracts';
import { createLogger } from '@/lib/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const log = createLogger('useSyncOrganization');

function syncSummary(result: SyncOrganizationResult): string {
  const parts: string[] = [];
  if (result.organizationNameUpdated) parts.push("nom de l'organisation mis à jour");
  if (result.sitesUpdated > 0)
    parts.push(
      `${result.sitesUpdated} centre${result.sitesUpdated > 1 ? 's' : ''} mis à jour`
    );
  if (result.sitesCreated > 0)
    parts.push(
      `${result.sitesCreated} centre${result.sitesCreated > 1 ? 's' : ''} créé${result.sitesCreated > 1 ? 's' : ''}`
    );
  return parts.length > 0
    ? `Synchronisation terminée : ${parts.join(', ')}`
    : 'Synchronisation terminée : tout est déjà à jour';
}

export function useSyncOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId }: { organizationId: string }) => {
      const res = await organizationClient.syncOrganizationFromSewan({
        params: { organizationId },
      });
      if (res.status !== 201) throw new Error('Failed to sync');
      return res.body;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.current() });
      toast.success(syncSummary(result));
    },
    onError: (error) => {
      toast.error('Erreur lors de la synchronisation');
      log.error('Failed to sync organization from Sewan', { error });
    },
  });
}
